import React, {useEffect, useMemo} from 'react'
import styles from './UIGameTimer.module.scss'
import clsx from 'clsx'
import {Timer} from '../../common/Timer/Timer'
import {lerp} from '@colonist/utils'
import {Sound} from '../../utils/sound'
import {useGameTimer} from '../../hooks/useGameTimer'
import {useGameStore} from '../../store/GameStoreContext'
import {ModeSettingEnum} from '@colonist/types'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'
import {ActionState} from '@colonist/game-states'

export interface UIGameTimerProps {
    timeLeftInSeconds: number
    isMyPlayerTakingAction: boolean
    isPaused: boolean
    timerStyle?: 'white' | 'beige'
    className?: string | undefined
}

export function UIGameTimer({
    timeLeftInSeconds,
    isMyPlayerTakingAction,
    isPaused,
    timerStyle = 'beige',
    className,
}: UIGameTimerProps) {
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const isColonistRush = modeSetting == ModeSettingEnum.ColonistRush4P

    const liveTimeLeftInSeconds = useGameTimer({
        timeLeftInSeconds: timeLeftInSeconds,
        isPaused: isPaused,
    })

    const SECONDS_LEFT_BEFORE_TICKING_SOUND = 5
    const currentStateValidator = useCurrentStateValidator()
    const myColor = useGameStore((state) => state.myColor)

    const isRedAndTicking = useMemo(() => {
        const isTimerInLastSeconds = liveTimeLeftInSeconds <= SECONDS_LEFT_BEFORE_TICKING_SOUND
        if(!isTimerInLastSeconds) return false

        if(!isMyPlayerTakingAction) return false

        const isPlayerAlsoPlayingNextTurn = isColonistRush
        const isMyPlayerActionStateNone = currentStateValidator.getPlayerActionState(myColor) == ActionState.None
        if(isPlayerAlsoPlayingNextTurn && isMyPlayerActionStateNone) return false

        return true
    }, [isMyPlayerTakingAction, liveTimeLeftInSeconds, currentStateValidator, myColor])

    const playTickingSound = (secondsLeft: number) => {
        if(!isRedAndTicking) return

        const isFirstReminder = secondsLeft == SECONDS_LEFT_BEFORE_TICKING_SOUND
        if(isFirstReminder) Sound.playSound(Sound.SoundType.FirstReminder, 0.6, 1.0)

        const progress = (SECONDS_LEFT_BEFORE_TICKING_SOUND - secondsLeft) / SECONDS_LEFT_BEFORE_TICKING_SOUND

        const minimumVolume = 1.0
        const maximumVolume = 1.2
        const volume = lerp(minimumVolume, maximumVolume, progress)

        const minimumPitch = 1.0
        const maximumPitch = 1.5
        const pitch = lerp(minimumPitch, maximumPitch, progress)

        Sound.playSound(Sound.SoundType.ClockTick, volume, pitch)
    }

    useEffect(() => {
        playTickingSound(liveTimeLeftInSeconds)
    }, [liveTimeLeftInSeconds])

    return <div className={clsx(styles.container, timerStyle === 'beige' && styles.beigeBackground, className, {
        [styles.hidden]: isBeginnerMode,
    })}>
        <Timer
            className={clsx({
                [styles.red]: isRedAndTicking,
                [styles.timerWhite]: timerStyle === 'white',
                [styles.timerBeige]: timerStyle === 'beige'})}
            timeLeftInSeconds={liveTimeLeftInSeconds}
        />
    </div>
}
